// CARO. Casa de estilo — Cloudflare Turnstile widget (explicit render).
//
// Renders the anti-bot challenge on public forms + checkout. Gated on the
// public site key: when VITE_TURNSTILE_SITE_KEY is unset the component renders
// nothing and calls nothing, so the forms behave exactly as before (and the
// Worker fail-opens when its secret is also unset). Reports the solved token to
// the parent via `onVerify`; pass a changing `resetSignal` to force a fresh
// token after a submit (Turnstile tokens are single-use).

import { useEffect, useRef } from 'react';

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

/** True when the client widget is configured (site key present at build time). */
export const TURNSTILE_ENABLED = !!SITE_KEY;

interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
      'timeout-callback'?: () => void;
    },
  ) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('turnstile_script_failed'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export function Turnstile({
  onVerify,
  resetSignal = 0,
}: {
  onVerify: (token: string) => void;
  resetSignal?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    if (!SITE_KEY) return;
    let cancelled = false;
    const mount = () =>
      loadScript()
        .then(() => {
          if (cancelled || !ref.current || !window.turnstile) return;
          widgetId.current = window.turnstile.render(ref.current, {
            sitekey: SITE_KEY,
            callback: (token) => onVerify(token),
            'error-callback': () => onVerify(''),
            'expired-callback': () => onVerify(''),
            'timeout-callback': () => onVerify(''),
          });
        })
        .catch(() => {
          // Script blocked/unreachable → leave the token empty. If a secret is
          // configured server-side the submit will be rejected (fail-closed);
          // if not, the server fail-opens.
        });
    // Load the challenge (~700 KB from challenges.cloudflare.com) only once the
    // widget nears the viewport OR the visitor first scrolls/touches/types: on
    // the home the newsletter band sits at the bottom and was competing with the
    // hero (LCP) for bandwidth on phones, while the first interaction still
    // leaves time to solve it before anyone reaches a form (which refuse to
    // submit without a token anyway).
    const TRIGGERS = ['scroll', 'pointerdown', 'keydown', 'touchstart'] as const;
    let io: IntersectionObserver | null = null;
    let started = false;
    const stop = () => {
      io?.disconnect();
      io = null;
      for (const ev of TRIGGERS) window.removeEventListener(ev, start);
    };
    function start() {
      if (started) return;
      started = true;
      stop();
      void mount();
    }
    if (ref.current && typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && start(), {
        rootMargin: '600px 0px',
      });
      io.observe(ref.current);
      for (const ev of TRIGGERS) window.addEventListener(ev, start, { passive: true });
    } else {
      start();
    }
    return () => {
      cancelled = true;
      stop();
      if (widgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          /* ignore */
        }
        widgetId.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (resetSignal && widgetId.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetId.current);
        onVerify('');
      } catch {
        /* ignore */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal]);

  if (!SITE_KEY) return null;
  return <div ref={ref} className="turnstile" style={{ marginTop: 'var(--space-2)' }} />;
}
